import React, { useState } from 'react';
import {
  Users,
  Calendar,
  DollarSign,
  AlertCircle,
  Download,
  Share2,
  FileText,
  TrendingUp,
  BarChart3,
} from 'lucide-react';

type ReportScope = 'students' | 'teachers' | 'finance' | 'school';
type TimeRange = 'daily' | 'weekly' | 'monthly' | 'yearly';

interface ReportConfig {
  scope: ReportScope;
  type: string;
  course: string;
  who: string;
  timeRange: TimeRange;
}

const reportTypesByScope: Record<ReportScope, { value: string; label: string }[]> = {
  students: [
    { value: 'performance', label: 'Daily Performance' },
    { value: 'attendance', label: 'Attendance Report' },
    { value: 'payments', label: 'Payments Report' },
    { value: 'progress', label: 'Academic Progress' },
  ],
  teachers: [
    { value: 'hours', label: 'Teaching Hours' },
    { value: 'performance', label: 'Class Performance' },
    { value: 'attendance', label: 'Attendance Logs' },
  ],
  finance: [
    { value: 'summary', label: 'Payments Summary' },
    { value: 'outstanding', label: 'Outstanding Balances' },
    { value: 'discounts', label: 'Discounts Breakdown' },
    { value: 'revenue', label: 'Revenue by Plan' },
  ],
  school: [
    { value: 'overall', label: 'Full School Performance' },
    { value: 'financial', label: 'Financial Health' },
    { value: 'attendance', label: 'Attendance Overview' },
  ],
};

const whoOptionsByScope: Record<ReportScope, { value: string; label: string }[]> = {
  students: [
    { value: 'all', label: 'All Students' },
    { value: 'individual', label: 'Individual Student' },
    { value: 'group', label: 'Selected Group / Class' },
  ],
  teachers: [
    { value: 'all', label: 'All Teachers' },
    { value: 'individual', label: 'Individual Teacher' },
  ],
  finance: [
    { value: 'all', label: 'All Students' },
    { value: 'pending', label: 'Students with Pending Payments' },
    { value: 'paid', label: 'Fully Paid Students Only' },
  ],
  school: [
    { value: 'school', label: 'Entire School' },
  ],
};

export function ReportsTab() {
  const [config, setConfig] = useState<ReportConfig>({
    scope: 'students',
    type: 'performance',
    course: 'all',
    who: 'all',
    timeRange: 'monthly',
  });
  const [reportGenerated, setReportGenerated] = useState(false);

  const handleScopeChange = (scope: ReportScope) => {
    setConfig({
      ...config,
      scope,
      type: reportTypesByScope[scope][0].value,
      who: whoOptionsByScope[scope][0].value,
    });
    setReportGenerated(false);
  };

  const handleGenerateReport = () => {
    setReportGenerated(true);
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <SummaryCard
          icon={<Users className="w-5 h-5 text-teal-600" />}
          label="Total Students"
          value="134"
          trend="+12 this month"
          color="teal"
        />
        <SummaryCard
          icon={<Calendar className="w-5 h-5 text-blue-600" />}
          label="Average Attendance"
          value="78%"
          trend="+5% from last month"
          color="blue"
        />
        <SummaryCard
          icon={<DollarSign className="w-5 h-5 text-green-600" />}
          label="Total Income"
          value="$7,012"
          trend="This month"
          color="green"
        />
        <SummaryCard
          icon={<AlertCircle className="w-5 h-5 text-orange-600" />}
          label="Pending Payments"
          value="17"
          trend="Requires attention"
          color="orange"
        />
      </div>

      {/* Report Configuration */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Configuration Panel */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Create a Report</h3>

          {/* Time Range */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Range
            </label>
            <select
              value={config.timeRange}
              onChange={(e) => setConfig({ ...config, timeRange: e.target.value as TimeRange })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          {/* Report Scope */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Scope
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['students', 'teachers', 'finance', 'school'] as ReportScope[]).map((scope) => (
                <button
                  key={scope}
                  onClick={() => handleScopeChange(scope)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    config.scope === scope
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {scope.charAt(0).toUpperCase() + scope.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Report Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <select
              value={config.type}
              onChange={(e) => {
                setConfig({ ...config, type: e.target.value });
                setReportGenerated(false);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {reportTypesByScope[config.scope].map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Course Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Course
            </label>
            <select
              value={config.course}
              onChange={(e) => {
                setConfig({ ...config, course: e.target.value });
                setReportGenerated(false);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Courses</option>
              <option value="math">Mathematics Advanced</option>
              <option value="physics">Physics Fundamentals</option>
              <option value="english">English Literature</option>
              <option value="chemistry">Chemistry 101</option>
            </select>
          </div>

          {/* Who Filter */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who
            </label>
            <select
              value={config.who}
              onChange={(e) => {
                setConfig({ ...config, who: e.target.value });
                setReportGenerated(false);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              disabled={config.scope === 'school'}
            >
              {whoOptionsByScope[config.scope].map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerateReport}
            className="w-full px-4 py-3 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center justify-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Create Report
          </button>
        </div>

        {/* Report Preview */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Report Preview</h3>
            {reportGenerated && (
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1">
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
                <button className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1">
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            )}
          </div>

          {reportGenerated ? (
            <div className="space-y-6">
              {/* Report Header */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900">
                  {reportTypesByScope[config.scope].find(t => t.value === config.type)?.label} Report
                </h4>
                <p className="text-sm text-gray-500">
                  Generated on {new Date().toLocaleDateString()} | {config.timeRange.charAt(0).toUpperCase() + config.timeRange.slice(1)} View
                </p>
              </div>

              {/* Mock Report Content */}
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-teal-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-teal-600">156</div>
                    <div className="text-sm text-gray-600">Total Records</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">89%</div>
                    <div className="text-sm text-gray-600">Completion Rate</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">+12%</div>
                    <div className="text-sm text-gray-600">vs Last Period</div>
                  </div>
                </div>

                {/* Chart Placeholder */}
                <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Chart visualization</p>
                  </div>
                </div>

                {/* Data Table Preview */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-gray-600">Name</th>
                        <th className="px-4 py-2 text-left text-gray-600">Value</th>
                        <th className="px-4 py-2 text-left text-gray-600">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-2">Mathematics Advanced</td>
                        <td className="px-4 py-2">92%</td>
                        <td className="px-4 py-2">
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Good</span>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Physics Fundamentals</td>
                        <td className="px-4 py-2">85%</td>
                        <td className="px-4 py-2">
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Good</span>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">English Literature</td>
                        <td className="px-4 py-2">78%</td>
                        <td className="px-4 py-2">
                          <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">Average</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-96 flex flex-col items-center justify-center text-gray-500">
              <FileText className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">No Report Generated</p>
              <p className="text-sm">Configure the options and click "Create Report"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  trend,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  trend: string;
  color: 'teal' | 'blue' | 'green' | 'orange';
}) {
  const colorStyles = {
    teal: 'bg-teal-50',
    blue: 'bg-blue-50',
    green: 'bg-green-50',
    orange: 'bg-orange-50',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${colorStyles[color]}`}>
          {icon}
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
      <div className="text-xs text-gray-400 mt-1">{trend}</div>
    </div>
  );
}
